import { useEffect, useRef, useState } from "react";
import { whatsappLink } from "@/config/company";
import {
  Search,
  Loader2,
  MapPin,
  CalendarClock,
  Clock3,
  MessageCircle,
  AlertTriangle,
  Hash,
} from "lucide-react";
import TrackLifecycle from "./TrackLifecycle";
import { STAGES } from "./stages";
import "./TrackPageView.css";

const HELP_URL = whatsappLink("Hi Sendtoo, I need help with my shipment");

/**
 * <TrackPageView /> — presentational "Track your shipment" page.
 * No fetching in here; everything arrives via props.
 *
 * Props:
 *   loading      boolean — search in flight (spinner + infinity shimmer)
 *   error        string | null — friendly message shown when set
 *   result       null | { number, status, stage, mode, state, origin,
 *                         destination, eta, updatedAt, events: [{stage,label,city,time}] }
 *   onSearch     (term: string) => void
 *   initialTerm  prefill for the search box
 *   replayKey    optional — change it to replay the infinity animation
 */
export default function TrackPageView({
  loading = false,
  error = null,
  result = null,
  onSearch,
  initialTerm = "",
  replayKey = 0,
}) {
  const [term, setTerm] = useState(initialTerm);
  useEffect(() => setTerm(initialTerm), [initialTerm]);

  const submit = (e) => {
    e.preventDefault();
    const v = term.trim();
    if (v && onSearch) onSearch(v);
  };

  const showResult = result && !error;

  // After a successful search, bring the journey animation into view so the
  // customer lands on their status instead of staring at the search box.
  const panelRef = useRef(null);
  useEffect(() => {
    if (!showResult || !panelRef.current) return undefined;
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const t = setTimeout(() => {
      try {
        panelRef.current.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
      } catch (e) {
        /* older browsers: no-op */
      }
    }, 350);
    return () => clearTimeout(t);
  }, [showResult, result && result.number, replayKey]);
  const lifeStage = showResult ? result.stage : null;
  const lifeMode = showResult ? result.mode : "road";
  const lifeState = showResult ? result.state : "normal";
  const lifeEvents = showResult ? result.events || [] : [];

  return (
    <div className="tl-page" data-testid="track-page-view">
      {/* hero: navy dot-grid, under a floating navbar (88px+ top padding) */}
      <section className="tl-hero">
        <div className="tl-container">
          <h1 className="font-heading tl-h1" data-testid="track-page-heading">
            Track your shipment
          </h1>
          <p className="tl-subtitle">Enter your Order ID, AWB or LR number for real-time status</p>

          <form className="tl-search-card" onSubmit={submit} data-testid="track-search-form">
            <Search size={20} className="tl-search-icon" aria-hidden="true" />
            <label htmlFor="tl-search-input" className="tl-sr-only">
              Enter your Order ID, AWB or LR number
            </label>
            <input
              id="tl-search-input"
              className="tl-search-input"
              placeholder="e.g. 24092672"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              autoComplete="off"
              data-testid="track-search-input"
            />
            <button type="submit" className="tl-track-btn" disabled={loading} data-testid="track-search-btn">
              {loading ? <Loader2 size={18} className="tl-spin" aria-hidden="true" /> : null}
              {loading ? "Tracking..." : "Track"}
            </button>
          </form>

          {error && (
            <div className="tl-error" role="alert" data-testid="track-error">
              <AlertTriangle size={18} aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          <div
            ref={panelRef}
            className={`tl-loop-panel ${loading ? "tl-shimmer" : ""}`}
            data-testid="track-lifecycle-panel"
          >
            <TrackLifecycle
              key={`${showResult ? result.number : "idle"}-${replayKey}`}
              stage={lifeStage}
              mode={lifeMode}
              state={lifeState}
              events={lifeEvents}
            />
            {!showResult && !loading && (
              <p className="tl-idle-hint" data-testid="track-idle-hint">
                Your shipment's journey will appear here.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* header switches to the dark logo once this reaches the top */}
      <div id="light-section-start" />

      {/* result area */}
      {showResult && (
        <section className="tl-results" data-testid="track-results">
          <div className="tl-container">
            <div className="tl-summary-card" data-testid="track-summary-card">
              <div className="tl-summary-top">
                <span className="tl-summary-number">
                  <Hash size={15} aria-hidden="true" /> {result.number}
                </span>
                <span className={`tl-chip tl-chip-${result.state}`} data-testid="track-status-chip">
                  {result.status}
                </span>
              </div>
              <div className="tl-summary-grid">
                {(result.origin || result.destination) && (
                  <p className="tl-summary-item">
                    <MapPin size={16} aria-hidden="true" />
                    <span>
                      {result.origin || "—"} <span aria-hidden="true">→</span> {result.destination || "—"}
                    </span>
                  </p>
                )}
                {result.eta && (
                  <p className="tl-summary-item">
                    <CalendarClock size={16} aria-hidden="true" />
                    <span>Expected: {result.eta}</span>
                  </p>
                )}
                <p className="tl-summary-item">
                  <Clock3 size={16} aria-hidden="true" />
                  <span>Last update: {result.updatedAt || "—"}</span>
                </p>
              </div>
            </div>

            {result.events && result.events.length > 0 && (
              <div className="tl-timeline-card" data-testid="track-timeline">
                <h2 className="font-heading tl-h2">Journey so far</h2>
                <ol className="tl-timeline">
                  {result.events.map((ev, i) => {
                    const EvIcon = (STAGES[ev.stage - 1] && STAGES[ev.stage - 1].icon) || MapPin;
                    return (
                      <li key={i} className={`tl-event ${i === 0 ? "tl-event-latest" : ""}`}>
                        <span className="tl-event-icon">
                          <EvIcon size={15} aria-hidden="true" />
                        </span>
                        <span className="tl-event-body">
                          <span className="tl-event-label">{ev.label}</span>
                          <span className="tl-event-meta">
                            {[ev.city, ev.time].filter(Boolean).join(" · ")}
                          </span>
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}

            <a
              href={HELP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="tl-help-strip"
              data-testid="track-help-strip"
            >
              <MessageCircle size={18} aria-hidden="true" />
              <span>
                Need help? <strong>Chat on WhatsApp</strong>
              </span>
            </a>
          </div>
        </section>
      )}
    </div>
  );
}
